import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowRight, CalendarDays, CheckCircle2, Clock3, Filter, Inbox, Mail, MapPin, PanelsTopLeft, Plus,
  Sparkles, Star, UsersRound, Video,
} from 'lucide-react';
import { Link } from '../navigation';
import { get, patch, post } from '../api';
import { useApp } from '../AppContext';
import { DashboardWellness, type WellnessData } from '../DashboardWellness';
import { Card, EmptyState, ErrorState, Loading, PageHeader, Progress, SectionHeader, Status, formatDate, formatMoney } from '../components';
import { QuickTaskComposer, TaskBoard, TaskCalendar, TaskSpreadsheet, TaskTimeline, TaskViewSwitcher, type TaskView } from '../TaskWorkspace';
import { taskDatePatch, type TaskDateKind } from '../task-drag';
import type { Paged, Task } from '../types';
import { MealAdminSummaryCard, MealBookingCard } from './TeamOperationsPages';
import { showUndo, type UndoOffer } from '../undo';

type Meeting = { id: number; title: string; starts_at: string; ends_at: string; location?: string | null; meeting_url?: string | null; organizer_name?: string; company_name?: string };
type ShiftDay = { date: string; kind: string; shiftName: string; startTime?: string; endTime?: string; holiday?: string | null; leaveId?: number | null };
type DashboardMailbox = {
  configured: boolean;
  emailAddress?: string | null;
  status: string;
  lastSyncAt?: string | null;
  counts: { inbox: number; unread: number; starred: number };
  messages: Array<{ uid: number; sender: string; subject: string; snippet: string; date?: string | null; unread: boolean; starred: boolean }>;
};
type DashboardData = {
  stats: Record<string, number>;
  attendance?: { checkin_at?: string | null; checkout_at?: string | null; status?: string; work_minutes?: number } | null;
  employee?: { job_title?: string; department_name?: string };
  weekSchedule?: ShiftDay[];
  calendar?: Meeting[];
  schedule?: Meeting[];
  mailbox?: DashboardMailbox;
};

function localDate(value: Date | string = new Date()) {
  const date = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  const year = date.getFullYear(); const month = String(date.getMonth() + 1).padStart(2, '0'); const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
function addDays(date: string, amount: number) {
  const value = new Date(`${date}T00:00:00`); value.setDate(value.getDate() + amount); return localDate(value);
}

export function DashboardPage() {
  const { bootstrap } = useApp();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [wellness, setWellness] = useState<WellnessData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState<TaskView>('spreadsheet');
  const [showCompleted, setShowCompleted] = useState(true);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(localDate());

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [taskData, dashboardData, wellnessData] = await Promise.all([
        get<Paged<Task>>('/api/tasks?limit=100'),
        get<{ role: string; data: DashboardData }>('/api/dashboard'),
        get<WellnessData>(`/api/wellness?date=${localDate()}`),
      ]);
      setTasks(taskData.items); setDashboard(dashboardData.data); setWellness(wellnessData);
    } catch (requestError) { setError((requestError as Error).message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function changeStatus(task: Task, status: string) {
    setTasks((current) => current.map((item) => item.id === task.id ? { ...item, status } : item));
    try {
      const result = await patch<{ item: Task; undo?: UndoOffer | null }>(`/api/tasks/${task.id}`, { status });
      showUndo(result.undo, () => void load()); window.dispatchEvent(new Event('nosivo:tasks-updated'));
    }
    catch (requestError) { setError((requestError as Error).message); await load(); }
  }

  async function createTask(title: string) {
    await post('/api/tasks', { title, assignedTo: bootstrap?.user.id || null, priority: 'medium', clientVisible: true });
    await load(); window.dispatchEvent(new Event('nosivo:tasks-updated'));
  }

  async function moveTaskDate(task: Task, kind: TaskDateKind, date: string) {
    const update = taskDatePatch(kind, date);
    setTasks((current) => current.map((item) => item.id === task.id ? {
      ...item,
      ...(update.plannedDate ? { planned_date: update.plannedDate } : {}),
      ...(update.dueDate ? { due_date: update.dueDate } : {}),
    } : item));
    try {
      const result = await patch<{ item: Task }>(`/api/tasks/${task.id}`, update);
      setTasks((current) => current.map((item) => item.id === task.id ? { ...item, ...result.item } : item));
      window.dispatchEvent(new Event('nosivo:tasks-updated'));
    } catch (requestError) { setError((requestError as Error).message); await load(); }
  }

  async function reorderTasks(items: Task[]) {
    setTasks(items);
    try {
      await patch('/api/tasks/reorder', { orderedIds: items.map((task) => task.id) });
      window.dispatchEvent(new Event('nosivo:tasks-updated'));
    } catch (requestError) { setError((requestError as Error).message); await load(); }
  }

  const visibleTasks = useMemo(() => (showCompleted ? tasks : tasks.filter((task) => task.status !== 'done')).slice(0, 50), [showCompleted, tasks]);
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, index) => addDays(localDate(), index)), []);
  const meetings = dashboard?.calendar || dashboard?.schedule || [];
  const agenda = useMemo(() => {
    const taskEvents = tasks.filter((task) => (task.due_date || task.planned_date) === selectedDate).map((task) => ({
      key: `task-${task.id}`, type: 'task' as const, title: task.title, meta: task.project_title || 'Assigned task', href: `/tasks/${task.id}`, status: task.status,
    }));
    const meetingEvents = meetings.filter((meeting) => localDate(meeting.starts_at) === selectedDate).map((meeting) => ({
      key: `meeting-${meeting.id}`, type: 'meeting' as const, title: meeting.title,
      meta: `${formatDate(meeting.starts_at, true)}${meeting.location ? ` · ${meeting.location}` : ''}`, href: '/meetings', status: 'scheduled',
    }));
    return [...meetingEvents, ...taskEvents];
  }, [meetings, selectedDate, tasks]);

  if (loading) return <Loading label="Preparing your complete workday" />;
  if ((!dashboard || !wellness) && error) return <ErrorState message={error} retry={() => void load()} />;
  if (!dashboard || !wellness) return null;

  const role = bootstrap?.user.role || 'employee';
  const calendarDays = role === 'employee' && dashboard.weekSchedule?.length
    ? dashboard.weekSchedule.map((day) => day.date)
    : weekDates;
  const subtitle = new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date());
  const workspaceTitle = role === 'employee' ? 'My assigned tasks' : role === 'client' ? 'Delivery tasks' : 'Company tasks';
  const stats = dashboard.stats || {};
  const summary = role === 'client' ? [
    { label: 'Active engagements', value: stats.activeProjects || 0, detail: 'Projects currently moving', tone: 'steel' },
    { label: 'Delivery queue', value: stats.openTasks || 0, detail: 'Work across your projects', tone: 'neutral' },
    { label: 'Decisions pending', value: stats.pendingApprovals || 0, detail: 'Waiting for your review', tone: 'attention' },
    { label: 'Outstanding', value: formatMoney(stats.outstanding || 0), detail: 'Current receivable balance', tone: 'neutral' },
  ] : [
    { label: 'Live projects', value: stats.activeProjects || 0, detail: 'Company delivery portfolio', tone: 'steel' },
    { label: 'Work in flight', value: stats.openTasks || 0, detail: `${stats.overdueTasks || 0} delivery exceptions`, tone: stats.overdueTasks ? 'critical' : 'positive' },
    { label: 'Workforce present', value: `${stats.checkedIn || 0}/${stats.activeEmployees || 0}`, detail: 'Checked in today', tone: 'positive' },
    { label: 'Decisions pending', value: stats.pendingApprovals || 0, detail: `${stats.pendingLeaves || 0} leave requests`, tone: 'attention' },
  ];
  const todayShift = dashboard.weekSchedule?.find((day) => day.date === localDate()) || dashboard.weekSchedule?.[0];
  const attendanceAction = dashboard.attendance?.checkin_at
    ? dashboard.attendance.checkout_at ? 'View today’s record' : 'Check out securely'
    : 'Check in securely';
  const mailbox = dashboard.mailbox;
  const agendaCard = <Card className="dashboard-agenda-card">
    <SectionHeader title={role === 'employee' ? 'Calendar & work schedule' : 'Calendar'}
      description={role === 'employee' ? 'Meetings, deadlines and shifts in one weekly view' : 'Meetings and deadlines across the next seven days'}
      action={role === 'employee' ? <span className="dashboard-calendar-actions"><Link to="/meetings">Full calendar</Link><Link to="/schedule">Shift calendar</Link></span> : <Link to="/meetings">Full calendar</Link>} />
    <div className={`dashboard-week-strip ${role === 'employee' ? 'dashboard-week-strip--with-shifts' : ''}`}>{calendarDays.map((date) => {
      const eventCount = tasks.filter((task) => (task.due_date || task.planned_date) === date).length + meetings.filter((meeting) => localDate(meeting.starts_at) === date).length;
      const value = new Date(`${date}T00:00:00`);
      const shiftDay = dashboard.weekSchedule?.find((day) => day.date === date);
      const shiftLabel = shiftDay?.kind === 'working' ? shiftDay.shiftName : shiftDay?.holiday || shiftDay?.shiftName || 'No shift';
      const shiftTime = shiftDay?.kind === 'working' ? `${shiftDay.startTime}–${shiftDay.endTime}` : shiftDay?.leaveId ? 'Approved leave' : 'No shift';
      return <button type="button" key={date} className={`${selectedDate === date ? 'is-selected' : ''} ${shiftDay?.kind && shiftDay.kind !== 'working' ? 'is-off' : ''}`} onClick={() => setSelectedDate(date)}>
        <small>{value.toLocaleDateString('en-IN', { weekday: 'short' })}</small><strong>{value.getDate()}</strong><span>{eventCount ? `${eventCount} item${eventCount === 1 ? '' : 's'}` : 'Free'}</span>
        {role === 'employee' && shiftDay ? <span className="dashboard-week-strip__shift"><b>{shiftLabel}</b><em>{shiftTime}</em></span> : null}
      </button>;
    })}</div>
    <div className="dashboard-agenda-list">{agenda.length ? agenda.slice(0, 5).map((item) => <Link to={item.href} key={item.key}><span className={`agenda-icon agenda-icon--${item.type}`}>{item.type === 'meeting' ? <Video size={15} /> : <CheckCircle2 size={15} />}</span><span><strong>{item.title}</strong><small>{item.meta}</small></span><Status value={item.status} /></Link>) : <EmptyState icon={Sparkles} title="A clear day" description="No meetings or task deadlines are planned for this date." />}</div>
  </Card>;
  const mailboxCard = <Card className="dashboard-mailbox-card">
    <SectionHeader title="Inbox preview" description={mailbox?.configured ? mailbox.emailAddress || 'Your connected mailbox' : 'Connect your work mailbox to see messages here'} action={<Link to="/email">Open mailbox</Link>} />
    <div className="dashboard-mailbox-counts" aria-label="Mailbox counts">
      <span><Mail /><small>Unread</small><strong>{mailbox?.counts.unread || 0}</strong></span>
      <span><Inbox /><small>Inbox</small><strong>{mailbox?.counts.inbox || 0}</strong></span>
      <span><Star /><small>Starred</small><strong>{mailbox?.counts.starred || 0}</strong></span>
    </div>
    {mailbox?.messages.length ? <div className="dashboard-mailbox-list" aria-label="Latest three inbox messages">
      {mailbox.messages.map((message) => <Link className={message.unread ? 'is-unread' : ''} key={message.uid} to={`/email?folder=inbox&message=${message.uid}`}>
        <span className="dashboard-mailbox-list__icon"><Mail size={15} /></span>
        <span><strong>{message.sender}</strong><b>{message.subject}</b><small>{message.snippet || 'Open this message to read more.'}</small></span>
        <time>{message.date ? formatDate(message.date, true) : '—'}</time>
      </Link>)}
    </div> : <div className="dashboard-mailbox-empty"><Mail size={19} /><span><strong>{mailbox?.configured ? 'Your inbox preview is clear' : 'Email Access is not connected yet'}</strong><small>{mailbox?.configured ? 'New messages will appear here after the next mailbox sync.' : 'Connect it once to see message counts and your latest three emails.'}</small></span></div>}
    {mailbox?.configured && <p className="dashboard-mailbox-sync">{mailbox.lastSyncAt ? `Last updated ${formatDate(mailbox.lastSyncAt, true)}` : 'Waiting for the first secure mailbox sync'}</p>}
  </Card>;
  const taskWorkspace = <section className="nv-task-workspace dashboard-task-workspace">
    <div className="dashboard-workspace-heading"><div><span className="eyebrow">Delivery workspace</span><h2>{workspaceTitle}</h2><p>Plan, review and update delivery without leaving the dashboard.</p></div><Link to="/tasks">View all tasks <ArrowRight size={14} /></Link></div>
    <div className="nv-task-toolbar"><TaskViewSwitcher value={view} onChange={setView} /><div className="nv-task-toolbar__right"><Link className="nv-task-toolbar__action" to="/tasks"><PanelsTopLeft />Workspace</Link><button type="button" className={`nv-task-toolbar__action ${!showCompleted ? 'is-active' : ''}`} onClick={() => setShowCompleted((value) => !value)}><Filter />{showCompleted ? 'Hide completed' : 'Show completed'}</button></div></div>
    {role !== 'client' && <QuickTaskComposer open={quickAddOpen} onOpenChange={setQuickAddOpen} onCreate={createTask} />}
    <div className="nv-task-stage">{view === 'spreadsheet' ? <TaskSpreadsheet tasks={visibleTasks} onStatus={role === 'client' ? undefined : (task, status) => void changeStatus(task, status)} onReorder={role === 'client' ? undefined : (items) => void reorderTasks(items)} compact emptyTitle="Your task sheet is clear" /> : view === 'board' ? <TaskBoard tasks={visibleTasks} onStatus={role === 'client' ? undefined : (task, status) => void changeStatus(task, status)} /> : view === 'calendar' ? <TaskCalendar tasks={visibleTasks} onMoveDate={role === 'client' ? undefined : (task, kind, date) => void moveTaskDate(task, kind, date)} /> : <TaskTimeline tasks={visibleTasks} />}</div>
  </section>;
  const commandStrip = <section className="dashboard-command-strip" aria-label="Operational summary">
    <header>
      <div><span className="eyebrow">Operational summary</span><strong>Current position</strong></div>
      <span className="dashboard-command-strip__live"><i /> Live data</span>
    </header>
    <div className="dashboard-summary-ledger">
      {summary.map((item) => <article className={`dashboard-summary-item dashboard-summary-item--${item.tone}`} key={item.label}>
        <small>{item.label}</small><strong>{item.value}</strong><span>{item.detail}</span>
      </article>)}
    </div>
  </section>;

  return <div className="dashboard-page">
    <PageHeader eyebrow={subtitle} title="Home"
      description="Your day, schedule, wellbeing and delivery work—organized in one place."
      actions={<div className="header-quick-actions"><Link className="button button--secondary button--md" to="/tasks">Open full task sheet <ArrowRight size={16} /></Link>{role !== 'client' && <button type="button" className="button button--primary button--md" onClick={() => setQuickAddOpen(true)}><Plus size={16} /> Add task</button>}</div>} />
    {error && <ErrorState message={error} retry={() => void load()} />}

    {role !== 'employee' && commandStrip}

    <div className={`dashboard-daily-grid ${role === 'employee' ? 'dashboard-daily-grid--employee' : ''}`}>
      <Card className="dashboard-today-card">
        <SectionHeader title={role === 'employee' ? 'Today at work' : role === 'client' ? 'Delivery pulse' : 'Team pulse'} description="Live context for today" />
        {role === 'employee' ? <div className="dashboard-today-workspace">
          <section className="dashboard-today-work-summary">
            <div className={`today-status-orb ${dashboard.attendance?.checkin_at && !dashboard.attendance?.checkout_at ? 'is-active' : ''}`}><span><Clock3 size={24} /></span><div><small>Attendance</small><strong>{dashboard.attendance?.checkin_at ? dashboard.attendance.checkout_at ? 'Workday complete' : 'Checked in' : 'Not checked in yet'}</strong><em>{dashboard.attendance?.checkin_at ? `Started ${formatDate(dashboard.attendance.checkin_at, true)}` : 'Face ID attendance is ready when you arrive'}</em></div></div>
            <div className="today-detail-list"><span><CalendarDays /><small>Today’s shift</small><strong>{todayShift?.kind === 'working' ? todayShift.shiftName : todayShift?.shiftName || 'No schedule'}</strong></span><span><Clock3 /><small>Working hours</small><strong>{todayShift?.kind === 'working' ? `${todayShift.startTime}–${todayShift.endTime}` : 'Rest day'}</strong></span><span><MapPin /><small>Work profile</small><strong>{dashboard.employee?.department_name || dashboard.employee?.job_title || 'Nosivo team'}</strong></span></div>
            <Link className="button button--primary button--md dashboard-attendance-action" to="/attendance">{attendanceAction} <ArrowRight size={14} /></Link>
          </section>
          <MealBookingCard compact embedded />
        </div> : <>
          <div className="pulse-summary"><span><UsersRound /></span><div><strong>{role === 'client' ? `${stats.activeProjects || 0} projects moving` : `${stats.checkedIn || 0} teammates present`}</strong><small>{role === 'client' ? `${stats.openTasks || 0} open delivery tasks` : `${stats.activeEmployees || 0} active employees · ${stats.pendingLeaves || 0} leave requests`}</small></div></div>
          <Progress value={role === 'client' ? Math.min(100, (stats.activeProjects || 0) * 25) : stats.activeEmployees ? (stats.checkedIn || 0) / stats.activeEmployees * 100 : 0} label={role === 'client' ? 'Active delivery portfolio' : 'Today’s attendance'} />
          <Link className="dashboard-card-link" to={role === 'client' ? '/projects' : '/attendance'}>See full overview <ArrowRight size={14} /></Link>
        </>}
      </Card>

      {role === 'employee' ? mailboxCard : agendaCard}
    </div>

    {role === 'employee' && taskWorkspace}
    {role === 'employee' && agendaCard}

    {bootstrap?.capabilities.admin && <MealAdminSummaryCard compact />}

    <DashboardWellness data={wellness} onChange={setWellness} onError={setError} />

    {role !== 'employee' && taskWorkspace}
  </div>;
}
